import { isValidElement, useMemo } from "react";
import { useIntl } from "react-intl";
import { useFilterContext } from "../contexts/FilterProvider";
import { basicSort } from "../utils/basicSort";
import { FILTER_SELECTION_TYPES } from "../constants/filterSelectionTypes";
import { capitalizeFirstLetter } from "app/utils/string/capitalize";
import { OPTION_MODE } from "../constants/optionMode";

const MIN_SEARCHABLE_OPTIONS = 20;

const OPTION_ORIGIN = {
	MANUAL: "manual",
	AUTO: "auto",
};

const isNonEmpty = value => value !== undefined && value !== null && String(value).trim() !== "";
const isJSX = element => isValidElement(element);

const getAllOptionsFromProduct = ({ valueGetter, labelGetter, product }) => {
	try {
		const rawOptionValue = valueGetter(product);
		const rawOptionLabel = labelGetter ? labelGetter(product, rawOptionValue) : rawOptionValue;
		if (rawOptionValue === undefined || rawOptionValue === null) {
			return [];
		}

		const optionValues = Array.isArray(rawOptionValue) ? rawOptionValue : [rawOptionValue];
		const optionLabels = Array.isArray(rawOptionLabel) ? rawOptionLabel : [rawOptionLabel];

		if (optionValues.length !== optionLabels.length) {
			return [];
		}

		return optionValues.filter(isNonEmpty).map((optionValue, index) => ({
			value: String(optionValue),
			label: isNonEmpty(optionLabels[index])
				? String(optionLabels[index])
				: String(optionValue),
		}));
	} catch {
		return [];
	}
};

const uniqueOptionsFromProducts = ({ products = [], valueGetter, labelGetter }) => {
	const map = new Map();

	products
		.flatMap(p => getAllOptionsFromProduct({ product: p, valueGetter, labelGetter }))
		.forEach(option => {
			if (!map.has(option.value)) {
				map.set(option.value, option);
			}
		});

	return Array.from(map.values());
};

const buildOptionOriginMap = ({ facetConfigs, products }) => {
	const byField = {};

	facetConfigs.forEach(config => {
		const {
			field,
			valueGetter,
			labelGetter,
			buildOptions,
			optionMode = OPTION_MODE.MANUAL_ONLY,
		} = config;

		const manualOptions = typeof buildOptions === "function" ? buildOptions(products) : [];
		const autoOptions = uniqueOptionsFromProducts({ products, valueGetter, labelGetter });

		const manualValues = new Set(
			manualOptions.map(option => String(option?.value ?? "").trim()).filter(Boolean)
		);

		const autoValues = new Set(
			autoOptions.map(option => String(option?.value ?? "").trim()).filter(Boolean)
		);

		const map = new Map();

		if (optionMode === OPTION_MODE.MANUAL_ONLY && manualOptions.length > 0) {
			manualValues.forEach(v => map.set(v, OPTION_ORIGIN.MANUAL));
		} else {
			manualValues.forEach(v => map.set(v, OPTION_ORIGIN.MANUAL));
			autoValues.forEach(v => {
				if (!map.has(v)) {
					map.set(v, OPTION_ORIGIN.AUTO);
				}
			});
		}

		if (map.size) {
			byField[field] = map;
		}
	});

	return byField;
};

const matcherByOptionOrigin = ({
	product,
	value,
	valueGetter,
	customMatcher,
	origin,
	activeFilters,
}) => {
	const v = String(value);

	if (origin === OPTION_ORIGIN.MANUAL && typeof customMatcher === "function") {
		return Boolean(customMatcher(product, [v], activeFilters));
	}

	const productValues = getAllOptionsFromProduct({ valueGetter, product }).map(o => o.value);
	return productValues.includes(v);
};

const productMatchesAllFilters = (product, activeFilters, facetConfigs, optionOriginMap) => {
	return facetConfigs.every(({ field, valueGetter, customMatcher }) => {
		const selectedOptions = (Array.isArray(activeFilters[field])
			? activeFilters[field]
			: [activeFilters[field]]
		).filter(isNonEmpty);

		if (selectedOptions.length === 0) {
			return true;
		}

		const fromValueToOrigin = optionOriginMap?.[field];

		return selectedOptions.some(optionValue =>
			matcherByOptionOrigin({
				product,
				value: optionValue,
				valueGetter,
				customMatcher,
				origin: fromValueToOrigin?.get(String(optionValue)) ?? OPTION_ORIGIN.AUTO,
				activeFilters,
			})
		);
	});
};

const countProductsForOption = ({
	value,
	field,
	filters,
	products,
	facetConfigs,
	optionOriginMap,
}) => {
	const simulated = { ...filters, [field]: [value] };
	return products.filter(p =>
		productMatchesAllFilters(p, simulated, facetConfigs, optionOriginMap)
	).length;
};

const defaultSort = (a, b) => {
	const aNum = Number(a.value);
	const bNum = Number(b.value);

	if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
		return basicSort(aNum, bNum);
	}
	return basicSort(a.label, b.label);
};

const assembleOptions = ({ manualOptions, autoOptions, optionMode }) => {
	if (optionMode === OPTION_MODE.MANUAL_ONLY && manualOptions?.length > 0) {
		return manualOptions;
	}

	if (manualOptions?.length > 0) {
		const seen = new Set(manualOptions.map(o => String(o?.value ?? "").trim()).filter(Boolean));

		const autoOptionsWithoutDuplicates = autoOptions.filter(
			option => !seen.has(String(option?.value ?? "").trim())
		);

		return [...autoOptionsWithoutDuplicates, ...manualOptions];
	}

	return autoOptions;
};

const formatOptions = ({
	options,
	formatMessage,
	formatLabel,
	buildOptionPrefix,
	facetConfigs,
	products,
	field,
	activeFilters,
	optionOriginMap,
}) => {
	if (!options || options.length === 0) {
		return [];
	}

	return options
		.map(option => {
			const {
				value,
				label,
				labelKey,
				labelKeyOptions,
				formatLabelOptions,
				labelPrefix = null,
			} = option;

			const labelToFormat = labelKey
				? formatMessage({ id: labelKey }, labelKeyOptions)
				: label;

			const capitalizedLabel = capitalizeFirstLetter(labelToFormat);

			const formattedLabel =
				typeof formatLabel === "function"
					? formatLabel({
							label: capitalizedLabel,
							formatLabelOptions,
					  })
					: capitalizedLabel;

			const prefix =
				typeof buildOptionPrefix === "function"
					? buildOptionPrefix({
							value,
							label: formattedLabel,
							formatLabelOptions,
					  })
					: labelPrefix;

			const count = countProductsForOption({
				value,
				field,
				filters: activeFilters,
				products,
				facetConfigs,
				optionOriginMap,
			});

			return {
				value: String(value).trim(),
				label: formattedLabel,
				labelPrefix: isJSX(prefix) ? prefix : null,
				count,
			};
		})
		.filter(option => option.value !== "" && option.label !== "");
};

/* ---------- Hook principal ---------- */

export const useFacets = externalFilters => {
	const { formatMessage, locale } = useIntl();
	const {
		facetConfigs,
		filters: filtersFromUrl,
		products,
		facets: facetsFromBackend,
	} = useFilterContext();

	const activeFilters = externalFilters || filtersFromUrl;

	const optionOriginMap = useMemo(() => {
		if (!facetConfigs || facetConfigs.length === 0) {
			return null;
		}

		return buildOptionOriginMap({ facetConfigs, products });
	}, [facetConfigs, products]);

	const filteredProducts = useMemo(() => {
		if (typeof products === "undefined" || products === null) {
			return [];
		}

		return products.filter(p =>
			productMatchesAllFilters(p, activeFilters, facetConfigs, optionOriginMap)
		);
	}, [products, activeFilters, facetConfigs, optionOriginMap]);

	const facets = useMemo(() => {
		if (facetsFromBackend) {
			return facetsFromBackend;
		}

		if (!Array.isArray(facetConfigs) || !products) {
			return {};
		}

		const entries = facetConfigs
			.map(cfg => {
				const {
					field,
					title,
					valueGetter,
					labelGetter,
					formatLabel,
					buildOptions,
					buildOptionPrefix,
					customSort,
					selectionMode = FILTER_SELECTION_TYPES.MULTIPLE,
					allowSearch = false,
					search = {},
					optionMode = OPTION_MODE.MANUAL_ONLY,
				} = cfg;

				const sortFn = typeof customSort === "function" ? customSort : defaultSort;

				const manualOptions =
					typeof buildOptions === "function" ? buildOptions(products) : null;

				const autoOptions = uniqueOptionsFromProducts({
					products,
					valueGetter,
					labelGetter,
				});

				const formattedManualOptions = formatOptions({
					options: manualOptions,
					formatMessage,
					formatLabel,
					buildOptionPrefix,
					facetConfigs,
					products,
					field,
					activeFilters,
					optionOriginMap,
				});

				const formattedAutoOptions = formatOptions({
					options: autoOptions,
					formatMessage,
					formatLabel,
					buildOptionPrefix,
					facetConfigs,
					products,
					field,
					activeFilters,
					optionOriginMap,
				}).sort(sortFn);

				const options = assembleOptions({
					manualOptions: formattedManualOptions,
					autoOptions: formattedAutoOptions,
					optionMode,
				});

				if (options.length <= 1) {
					return null;
				}

				const isSearchable = allowSearch && options.length >= MIN_SEARCHABLE_OPTIONS;

				return [field, { title, selectionMode, options, isSearchable, search }];
			})
			.filter(Boolean);

		return Object.fromEntries(entries);
	}, [
		products,
		activeFilters,
		facetConfigs,
		formatMessage,
		locale,
		facetsFromBackend,
		optionOriginMap,
	]);

	return { filteredProducts, facets, activeFilters };
};
